import { atom, useAtom } from 'jotai';
import { atomWithDefault, useHydrateAtoms } from 'jotai/utils';

import { TrialInfoStatus } from '../../../Common/enums';
import { IUserDto, Optional } from '../../../Common/interfaces';
import { Log } from '../../../Common/Log';
import { reportExerciseProgress as reportExerciseProgressAPI } from '../exerciseAPI';
import {
    ExerciseDto,
    ExerciseStatus,
    ModuleDto,
    ReportExerciseProgressRequest,
} from '../interfaces';
import { addPageNumber } from './addPageNumber';
import {
    requestedExerciseCodeAtom,
    requestedModuleIDAtom,
    requestedPageNumberAtom,
} from './parseURL';

type HydrateDataProps = {
    module: ModuleDto;
    exercises: ExerciseDto[];
    trialInfoStatus: Optional<TrialInfoStatus>;
    user: IUserDto;
    children: React.ReactElement;
};

export const trialInfoStatusAtom = atom<Optional<TrialInfoStatus>>();
export const isTrialModeAtom = atom((get) => get(trialInfoStatusAtom) != null);
export const userAtom = atom<IUserDto>();
export const moduleAtom = atom<ModuleDto>();
export const exercisesAtom = atom<ExerciseDto[]>();
export const nextExerciseAtom = atom<ExerciseDto>((get) =>
    get(exercisesAtom).find((x) => x.Status != ExerciseStatus.Completed),
);
export const currentSegmentAtom = atomWithDefault(
    (get) => get(nextExerciseAtom)?.Segment ?? 1,
);
export const exercisesForCurrentSegmentAtom = atom((get) => {
    const currentSegment = get(currentSegmentAtom);
    return get(exercisesAtom).filter((x) => x.Segment == currentSegment);
});
export const currentExerciseAtom = atom<ExerciseDto>((get) => {
    const exerciseCode = get(requestedExerciseCodeAtom);
    const pageNumber = get(requestedPageNumberAtom);
    const exercises = get(exercisesAtom);

    if (exerciseCode == null) {
        return null;
    }

    const exercise = exercises.find((x) => x.Code === exerciseCode);
    if (exercise == null) {
        Log.error(`Unknown exercise code: ${exerciseCode}`);
    }
    return addPageNumber(exercise, pageNumber);
});
export const reportExerciseProgressAtom = atom(
    null,
    async (
        get,
        set,
        {
            exercise,
            updateRequest,
        }: {
            exercise: ExerciseDto;
            updateRequest: ReportExerciseProgressRequest;
        },
    ) => {
        const oldExercises = get(exercisesAtom);
        if (updateRequest.Data) {
            const oldExerciseIdx = oldExercises.findIndex(
                (x) => x.Id === exercise.Id,
            );
            const oldExercise = oldExercises[oldExerciseIdx];
            if (oldExercise) {
                const u = oldExercises.slice();
                u[oldExerciseIdx] = {
                    ...oldExercise,
                    Data: {
                        ...oldExercise.Data,
                        ...updateRequest.Data,
                    },
                };
                set(exercisesAtom, u);
            }
        }
        try {
            const exercises = await reportExerciseProgressAPI(
                exercise.ModuleId.Value,
                exercise.Id,
                updateRequest,
            );
            set(exercisesAtom, exercises);
        } catch (e) {
            set(exercisesAtom, oldExercises);
            throw e;
        }
    },
);

export function HydrateData({
    module,
    exercises,
    trialInfoStatus,
    user,
    children,
}: HydrateDataProps) {
    const [moduleID] = useAtom(requestedModuleIDAtom);
    if (moduleID !== module.Id) {
        Log.error(
            `Invalid module ID: ${moduleID}. Server loaded us with ${module.Id}`,
        );
    }

    useHydrateAtoms([
        [moduleAtom, module],
        [exercisesAtom, exercises],
        [trialInfoStatusAtom, trialInfoStatus],
        [userAtom, user],
    ]);
    return children;
}
